// CB:BEGIN invite-preview-component
import React, { useState, useEffect } from 'react';
import { useApi } from '@/lib/useApi';
import { useUser } from '@clerk/clerk-react';

interface RedactedProject {
  id: string;
  status: string;
  projectType: string;
  city?: string;
  state?: string;
  zip?: string;
  extraInfo?: string;
  desiredStart?: string;
  createdAt: string;
  hasExistingBid: boolean;
  existingBid?: any;
}

interface InvitePreviewProps {
  onNavigate?: (screen: string) => void;
}

const InvitePreview: React.FC<InvitePreviewProps> = ({ onNavigate }) => {
  const api = useApi();
  const { isSignedIn, isLoaded } = useUser();

  // CB:BEGIN fr-003-extract-token-enhanced
  // Extract token from URL pathname with defensive parsing
  // Handles query params, trailing slashes, and safe-link wrappers
  function extractTokenFromUrl(): string | null {
    console.log('[INVITE] 🔍 Extracting token from URL:', window.location.href);

    const url = new URL(window.location.href);
    const pathParts = url.pathname.split('/').filter(Boolean);

    console.log('[INVITE] Path parts:', pathParts);

    const inviteIndex = pathParts.indexOf('invite');
    console.log('[INVITE] Invite index:', inviteIndex, 'of', pathParts.length);

    if (inviteIndex >= 0 && inviteIndex < pathParts.length - 1) {
      let token = decodeURIComponent(pathParts[inviteIndex + 1]);
      console.log('[INVITE] Raw token:', token);

      // Strip query params and fragments
      token = token.split('?')[0].split('#')[0];
      console.log('[INVITE] After cleanup:', token);

      // Extract only hex characters
      const hexMatch = token.match(/^[a-f0-9]{32,128}/i);
      console.log('[INVITE] Hex match:', hexMatch ? 'YES' : 'NO', 'Length:', token.length);

      if (hexMatch) {
        token = hexMatch[0];
      }

      // Validate token format
      if (token.length >= 32 && /^[a-f0-9]+$/i.test(token)) {
        console.log('[INVITE] ✅ Valid token extracted:', token.substring(0, 16) + '...');
        return token;
      } else {
        console.log('[INVITE] ❌ Token validation failed. Length:', token.length, 'IsHex:', /^[a-f0-9]+$/i.test(token));
      }
    } else {
      console.log('[INVITE] ❌ Could not find invite token in path');
    }

    console.log('[INVITE] ❌ Returning null');
    return null;
  }
  // CB:END fr-003-extract-token-enhanced

  const [token, setToken] = useState<string | null>(null);

  const [inviteData, setInviteData] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reportLoading, setReportLoading] = useState(false);

  // Extract token from URL
  useEffect(() => {
    console.log('[INVITE] useEffect: Extracting token from URL...');
    const extractedToken = extractTokenFromUrl();

    if (extractedToken) {
      console.log('[INVITE] ✅ Token set:', extractedToken.substring(0, 8) + '...');
      setToken(extractedToken);
    } else {
      console.error('[INVITE] ❌ Failed to extract token from URL:', window.location.href);
      setError(`Invalid invite link. URL: ${window.location.pathname}`);
      setLoading(false);
    }
  }, []);

  // Load invite data
  useEffect(() => {
    if (!token) return;

    const loadInvite = async () => {
      try {
        setLoading(true);
        console.log('[INVITE] 🔧 Fetching invite preview for token:', token.substring(0, 8) + '...');
        
        // Use useApi hook for consistent URL building and error handling
        // The preview endpoint is public (no auth required), but useApi handles it gracefully
        const data = await api.get(`/api/invites/${token}/preview`);
        
        console.log('[INVITE] ✅ Invite data loaded:', data);
        setInviteData(data);
        setError(null);

      } catch (err: any) {
        const errorCode = err.errorCode ?? err.response?.data?.errorCode;
        const serverMessage = err.response?.data?.error ?? err.message;
        const message =
          errorCode === 'EXPIRED'
            ? 'This invite link has expired. Please contact the sender for a new invite.'
            : errorCode === 'REVOKED'
              ? 'This invite has been revoked. Please contact the sender for a new invite.'
              : errorCode === 'ALREADY_USED'
                ? 'This invite has already been used. If you submitted a bid, you can access the project from your dashboard.'
                : errorCode === 'RATE_LIMITED'
                  ? 'Too many requests. Please wait a minute and try again.'
                  : errorCode === 'NOT_FOUND' || errorCode === 'INVALID_TOKEN'
                    ? 'Invalid or expired invite link. Please check the URL or contact the sender.'
                    : serverMessage || 'Failed to load invite';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    loadInvite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]); // Only depend on token - api object is stable and doesn't need to be in deps

  // CB:BEGIN auto-redirect-after-auth
  // Handle auto-redirect after signup/login
  useEffect(() => {
    if (!token || !inviteData) return;

    const urlParams = new URLSearchParams(window.location.search);
    const autoRedirect = urlParams.get('auto') === '1';

    if (!autoRedirect) return; // Only proceed if auto=1 is present

    console.log('[INVITE] 🔄 Auto-redirect mode detected, checking auth status...');
    
    const resolveInvite = async (): Promise<'success' | 'retry' | 'error'> => {
      try {
        console.log('[INVITE] 🔍 Calling resolve endpoint...');
        const result = await api.post(`/api/invites/${token}/resolve`, {});
        
        if (result.success && result.projectId && result.redirectUrl) {
          console.log('[INVITE] ✅ Invite resolved, redirecting to:', result.redirectUrl);
          
          // Store project ID in localStorage for potential use by contractor dashboard
          try {
            localStorage.setItem('active_project_id', result.projectId);
          } catch (e) {
            console.warn('[INVITE] Failed to set localStorage:', e);
          }
          
          // Redirect to project bidding page
          window.location.href = result.redirectUrl;
          return 'success';
        } else {
          console.error('[INVITE] ❌ Invalid resolve response:', result);
          setError('Failed to resolve invite. Please try refreshing the page.');
          return 'error';
        }
      } catch (err: any) {
        console.error('[INVITE] ❌ Failed to resolve invite:', err);
        
        // Show user-friendly error messages
        const errorCode = err.errorCode || err.error?.errorCode;
        const errorMessage = err.error || err.message || 'Unknown error';
        const status = err.status || err.response?.status;
        
        // Check if this is an auth error that should be retried
        const isAuthError = status === 401 || 
                           errorCode === 'UNAUTHORIZED' || 
                           errorMessage.includes('Authentication') || 
                           errorMessage.includes('required') ||
                           errorMessage.includes('User email not found');
        
        if (isAuthError) {
          // If not authenticated yet, wait and retry (Clerk session might still be establishing)
          console.log('[INVITE] ⏳ Not authenticated yet (401), will retry...');
          return 'retry'; // Signal to retry
        }
        
        // Non-retryable errors - show error message
        if (errorCode === 'EMAIL_MISMATCH') {
          setError('This invite was sent to a different email address. Please sign in with the email address that received the invite.');
        } else if (errorCode === 'EXPIRED') {
          setError('This invite link has expired. Please contact the homeowner for a new invite.');
        } else if (errorCode === 'REVOKED') {
          setError('This invite has been revoked. Please contact the homeowner for a new invite.');
        } else if (errorCode === 'ALREADY_USED') {
          setError('This invite has already been used. If you have already submitted a bid, you can access the project from your dashboard.');
        } else {
          setError(`Unable to access project: ${errorMessage}`);
        }
        return 'error'; // Signal permanent failure
      }
    };

    // Retry logic: wait for Clerk session to be ready
    const attemptResolve = async (attempt = 0, maxAttempts = 15) => {
      if (attempt >= maxAttempts) {
        console.error('[INVITE] ❌ Max retry attempts reached, giving up');
        setError('Authentication is taking longer than expected. Please refresh the page or try signing in again.');
        return;
      }

      // Wait for Clerk to finish loading before checking auth status
      if (!isLoaded) {
        console.log(`[INVITE] ⏳ Clerk still loading (attempt ${attempt + 1}/${maxAttempts}), waiting...`);
        setTimeout(() => {
          attemptResolve(attempt + 1, maxAttempts);
        }, 300);
        return;
      }

      // If user is signed in, try to resolve
      if (isSignedIn) {
        console.log(`[INVITE] ✅ User is signed in (attempt ${attempt + 1}), resolving invite...`);
        const result = await resolveInvite();
        
        if (result === 'success') {
          return; // Success, redirect happened
        } else if (result === 'retry') {
          // Auth error - continue retrying
          console.log(`[INVITE] ⚠️ Auth error, retrying... (attempt ${attempt + 1}/${maxAttempts})`);
        } else {
          // Permanent error - stop retrying
          console.error('[INVITE] ❌ Permanent error, stopping retries');
          return;
        }
      } else {
        console.log(`[INVITE] ⏳ User not signed in yet (attempt ${attempt + 1}/${maxAttempts}), waiting...`);
      }

      // Retry after delay (only if we haven't hit max attempts)
      if (attempt < maxAttempts - 1) {
        setTimeout(() => {
          attemptResolve(attempt + 1, maxAttempts);
        }, 500); // Wait 500ms between attempts
      }
    };

    // Start attempting after a small initial delay to allow Clerk to initialize
    setTimeout(() => {
      attemptResolve();
    }, 800);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, inviteData, isSignedIn, isLoaded]); // api is stable, doesn't need to be in deps
  // CB:END auto-redirect-after-auth

  // Check for ?view=redacted query param
  useEffect(() => {
    if (!token || !inviteData) return;

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('view') === 'redacted') {
      console.log('🔧 Auto-opening report from query param');
      // Small delay to ensure component is mounted
      setTimeout(() => {
        handleViewReport();
      }, 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, inviteData]); // handleViewReport is stable, doesn't need to be in deps
  
  // CB:BEGIN fr-003-handle-view-report
  const handleViewReport = async () => {
    if (!token || reportLoading) return;

    try {
      setReportLoading(true);
      console.log('[INVITE] 🔧 Fetching redacted report URL for token:', token.substring(0, 8) + '...');
      
      // Use useApi hook for consistent URL building and error handling
      const data = await api.get(`/api/invites/${token}/redacted-report-url`);
      
      console.log('[INVITE] ✅ Report URL received:', data.type);

      if (data.url) {
        window.open(data.url, '_blank', 'noopener,noreferrer');
      }

    } catch (err: any) {
      console.error('[INVITE] ❌ Failed to open report:', err);
      alert(`Failed to open report: ${err.message || 'Unknown error'}`);
    } finally {
      setReportLoading(false);
    }
  };
  // CB:END fr-003-handle-view-report

  // Render states
  if (loading) {
    return (
      <div className="container light-mode-override" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading invite...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container light-mode-override" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-2xl w-full">
          <div className="text-red-600 text-5xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Invalid or Unavailable Invite</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500 mb-4">
            If the link has expired or been revoked, please contact the sender for a new invite.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="btn btn-primary"
            style={{ marginTop: '24px', width: '100%' }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!inviteData) {
    return null;
  }

  const { project, invite } = inviteData;
  const expiresAt = new Date(invite.expiresAt);
  const isExpiringSoon = expiresAt.getTime() - Date.now() < 24 * 60 * 60 * 1000; // < 24h

  return (
    <div className="container light-mode-override">
      {/* Simple header matching MVP style */}
      <div className="header" style={{ background: '#123c3b', color: 'white', padding: '20px 30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="logo" style={{ fontSize: 24, fontWeight: 600, letterSpacing: -0.5, cursor: 'pointer' }} onClick={() => {
          // CB:BEGIN fix-logo-navigation
          if (onNavigate) {
            onNavigate('landing');
          } else {
            window.location.href = '/';
          }
          // CB:END fix-logo-navigation
        }}>
          CoreBids
        </div>
      </div>

      <div className="content" style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 20px' }}>
        {/* Header Card */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            You're Invited to Bid
          </h1>
          <p className="text-gray-600">
            {project.homeowner?.displayName || 'Homeowner'} has invited you to submit a bid for their project.
          </p>
          {isExpiringSoon && (
            <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded p-3">
              <p className="text-yellow-800 text-sm">
                ⏰ This invite expires in less than 24 hours ({expiresAt.toLocaleString()})
              </p>
            </div>
          )}
        </div>

        {/* Project Details Card - no PII (address withheld in preview) */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {project.projectType === 'ROOF' ? 'Roofing Project' : (project.projectType || 'Project')}
          </h2>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-sm text-gray-500">Location</p>
              <p className="font-medium text-gray-900">
                {[project.city, project.state].filter(Boolean).join(', ') || 'Location withheld'}
              </p>
              {project.zipCode && (
                <p className="text-sm text-gray-600">ZIP: {project.zipCode}</p>
              )}
            </div>
            <div>
              <p className="text-sm text-gray-500">Project Type</p>
              <p className="font-medium text-gray-900">{project.projectType}</p>
            </div>
          </div>

          {project.description && (
            <div className="mb-4">
              <p className="text-sm text-gray-500 mb-1">Description</p>
              <p className="text-gray-700">{project.description}</p>
            </div>
          )}

          {project.hasRoofReport && (
            <div className="mt-6">
              <button
                onClick={handleViewReport}
                disabled={reportLoading}
                className="btn btn-primary"
                style={{ width: '100%', padding: '12px 24px' }}
              >
                {reportLoading ? 'Opening Report...' : '📄 View Redacted Report'}
              </button>
              <p className="text-xs text-gray-500 mt-2 text-center">
                Report contains property measurements and condition assessment (sensitive information redacted)
              </p>
            </div>
          )}
        </div>

        {/* Next Steps Card */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Ready to Submit a Bid?
          </h3>
          <p className="text-gray-600 mb-4">
            Create your contractor account to submit a bid for this project. You'll be able to review the full project details and submit your competitive bid after signing up.
          </p>
          <button 
            onClick={() => {
              if (token) {
                // CB:BEGIN fix-contractor-signup-navigation
                // Use window.location.href to preserve query params and trigger URL-based routing
                window.location.href = `/contractor-start?inviteToken=${encodeURIComponent(token)}`;
                // CB:END fix-contractor-signup-navigation
              } else {
                console.error('[INVITE] ❌ Cannot navigate: token is null');
                alert('Invalid invite token. Please refresh the page.');
              }
            }}
            className="btn btn-primary"
            style={{ width: '100%', padding: '12px 24px' }}
          >
            Create Account & Submit Bid →
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvitePreview;
// CB:END invite-preview-component


